import { useEffect, useRef, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { useOrganization } from "@/hooks/useOrganization";
import { useAccessContext } from "@/hooks/useAccessContext";
import { useMfaRequirement } from "@/hooks/useMfaRequirement";
import { createSessionVerificationTimeoutError, useLoadingTimeout } from "@/hooks/useLoadingTimeout";
import { ForgePageLoader } from "@/components/ForgeLoader";
import { startPerformanceMark } from "@/lib/performance";
import {
  logAuthTransition,
  trackRoute,
} from "@/lib/auth-transition-logger";
import {
  getAuthRedirectReason,
  getAuthRedirectTarget,
  hasExplicitSignedOutState,
} from "@/components/layout/routeGuardUtils";

interface ProtectedRouteProps {
  children: React.ReactNode;
  skipOrgCheck?: boolean;
  skipMfaCheck?: boolean;
  skipAccessError?: boolean;
}

// Auth bootstrap can take up to ~10s + retry overhead. Keep the auth timeout
// reasonably tight so genuinely broken sessions still fail fast.
const AUTH_ROUTE_GUARD_LOADING_TIMEOUT_MS = 20_000;

// Permissions + MFA readiness can include several chained network checks
// (organization, roles/scopes, factor discovery, and optional reauth state).
// Give this phase extra headroom to avoid false-positive session errors on
// slower clients/connections.
const CONTEXT_ROUTE_GUARD_LOADING_TIMEOUT_MS = 45_000;

interface AuthenticatedProtectedRouteContentProps extends ProtectedRouteProps {
  location: ReturnType<typeof useLocation>;
  guardReadyMarkRef: React.MutableRefObject<ReturnType<typeof startPerformanceMark> | null>;
  guardReadyLoggedRef: React.MutableRefObject<boolean>;
}

function AuthenticatedProtectedRouteContentBypassedMfa({
  children,
}: {
  children: React.ReactNode;
}) {
  return <>{children}</>;
}

function AuthenticatedProtectedRouteContentWithMfa({
  children,
  skipOrgCheck,
  skipMfaCheck,
  skipAccessError,
  location,
  guardReadyMarkRef,
  guardReadyLoggedRef,
}: AuthenticatedProtectedRouteContentProps) {
  const { needsOnboarding, isLoading: orgLoading, organization, isOrgAdmin, error: orgError } = useOrganization();
  const { data: accessContext, isLoading: accessLoading, isError: accessError } = useAccessContext();
  const isSuperAdmin = accessContext?.capabilities.isSuperAdmin ?? false;
  const { status: mfaStatus, needsChallenge, needsSetup, requiresMfa, reauthRequired } = useMfaRequirement();

  const mfaReady = mfaStatus === "checked";
  const isLoading = orgLoading || accessLoading || (!skipMfaCheck && !mfaReady);
  const isLoadingTimedOut = useLoadingTimeout(isLoading, CONTEXT_ROUTE_GUARD_LOADING_TIMEOUT_MS);

  // If we have a previously-selected org, don't bounce to onboarding due to transient org fetch issues.
  const hasStoredOrg =
    !skipOrgCheck &&
    typeof window !== "undefined" &&
    !!localStorage.getItem("forge_active_org");

  // User is authenticated — now wait for org, roles, and MFA to load.
  // When skipMfaCheck is true (e.g., /mfa/challenge, /mfa/setup), don't block
  // on MFA readiness — the MFA pages themselves handle MFA state internally.
  // Blocking here creates a deadlock: the user needs the MFA page to complete
  // MFA, but the page waits for MFA status to resolve first.
  if (isLoading && isLoadingTimedOut) {
    throw createSessionVerificationTimeoutError(
      "Session verification is taking longer than expected while loading permissions and MFA status.",
    );
  }

  if (isLoading) {
    return <ForgePageLoader />;
  }

  // If role/permission loading failed, throw to error boundary unless this route
  // is managed by additional role checks (e.g. staff area). In that case, we let the
  // downstream gate decide and avoid a hard page crash.
  if (accessError && !skipAccessError) {
    const error = new Error("Unable to verify your permissions. Please reload or try again.");
    error.name = "SessionVerificationError";
    throw error;
  }

  if (orgError && !skipOrgCheck) {
    const error = new Error("Unable to load your organization. Please reload or try again.");
    error.name = "SessionVerificationError";
    throw error;
  }

  if (!skipMfaCheck && requiresMfa && mfaReady) {
    if (needsSetup && location.pathname !== "/mfa/setup") {
      return <Navigate to="/mfa/setup" state={{ from: location }} replace />;
    }

    if (needsChallenge && location.pathname !== "/mfa/challenge") {
      const challengePath = reauthRequired ? "/mfa/challenge?reauth=1" : "/mfa/challenge";
      return <Navigate to={challengePath} state={{ from: location }} replace />;
    }
  }

  // Redirect to onboarding only when we are confident the user truly has no org.
  // Super admins may not have organization_members rows — skip the redirect for them.
  if (
    !skipOrgCheck &&
    needsOnboarding &&
    !hasStoredOrg &&
    !isSuperAdmin &&
    location.pathname !== "/onboarding"
  ) {
    return <Navigate to="/onboarding" replace />;
  }

  if (
    organization?.status === "disabled" &&
    !isOrgAdmin &&
    !isSuperAdmin &&
    location.pathname !== "/disabled"
  ) {
    return <Navigate to="/disabled" replace />;
  }

  if (!guardReadyLoggedRef.current && guardReadyMarkRef.current) {
    guardReadyLoggedRef.current = true;
    guardReadyMarkRef.current.end();
  }

  return <>{children}</>;
}

function AuthenticatedProtectedRouteContent({
  children,
  skipOrgCheck,
  skipMfaCheck,
  skipAccessError,
  location,
  guardReadyMarkRef,
  guardReadyLoggedRef,
}: AuthenticatedProtectedRouteContentProps) {
  if (skipMfaCheck) {
    return <AuthenticatedProtectedRouteContentBypassedMfa>{children}</AuthenticatedProtectedRouteContentBypassedMfa>;
  }

  return (
    <AuthenticatedProtectedRouteContentWithMfa
      skipOrgCheck={skipOrgCheck}
      skipMfaCheck={skipMfaCheck}
      skipAccessError={skipAccessError}
      location={location}
      guardReadyMarkRef={guardReadyMarkRef}
      guardReadyLoggedRef={guardReadyLoggedRef}
    >
      {children}
    </AuthenticatedProtectedRouteContentWithMfa>
  );
}

export function ProtectedRoute({
  children,
  skipOrgCheck = false,
  skipMfaCheck = false,
  skipAccessError = false,
}: ProtectedRouteProps) {
  const guardReadyMarkRef = useRef<ReturnType<typeof startPerformanceMark> | null>(null);
  const guardReadyLoggedRef = useRef(false);
  if (!guardReadyMarkRef.current) {
    guardReadyMarkRef.current = startPerformanceMark("guard:protected-route-ready");
  }

  const {
    isAuthenticated,
    isLoading: authLoading,
    hasSessionError,
    authError,
    lastAuthEvent,
    rehydrateSession,
  } = useAuth();

  const location = useLocation();
  const currentRoute = `${location?.pathname || ""}${location?.search || ""}${location?.hash || ""}`;
  const [hasTriedRehydrate, setHasTriedRehydrate] = useState(false);
  const [isRehydrating, setIsRehydrating] = useState(false);
  const [hasTriedTimeoutRehydrate, setHasTriedTimeoutRehydrate] = useState(false);

  // Track route changes for auth transition logging
  useEffect(() => {
    trackRoute(currentRoute);
  }, [currentRoute]);

  // Redirect unauthenticated users quickly, but optionally attempt one targeted
  // rehydrate pass for known transient session states.
  //
  // With @supabase/ssr cookie-based sessions, the initial bootstrap can
  // occasionally return null due to timing (cookies not yet parsed, token
  // refresh in-flight, or cross-tab interference).  Rather than immediately
  // bouncing to /auth — which creates the "session drops on every navigation"
  // problem — always try one rehydrate pass before giving up.  This is cheap
  // (single getSession() call) and eliminates most false-positive logouts.
  const shouldAttemptRehydrateBeforeRedirect =
    !isAuthenticated &&
    !authLoading &&
    (!hasSessionError || lastAuthEvent === "INITIAL_SESSION") &&
    !hasTriedRehydrate;

  const hasConfirmedSignedOutState = hasExplicitSignedOutState(
    isAuthenticated,
    lastAuthEvent,
  );

  useEffect(() => {
    if (!shouldAttemptRehydrateBeforeRedirect) {
      return;
    }

    let active = true;
    setIsRehydrating(true);

    void (async () => {
      try {
        await rehydrateSession();
      } finally {
        if (active) {
          setHasTriedRehydrate(true);
          setIsRehydrating(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [rehydrateSession, shouldAttemptRehydrateBeforeRedirect]);

  const isAuthLoading = authLoading || isRehydrating || shouldAttemptRehydrateBeforeRedirect;
  const isAuthLoadingTimedOut = useLoadingTimeout(
    isAuthLoading,
    AUTH_ROUTE_GUARD_LOADING_TIMEOUT_MS,
  );

  // When auth loading times out, attempt one last rehydration before crashing
  // to the error boundary.  This covers edge cases where the bootstrap retry
  // (with the increased 10 s timeout) completes just after the guard timer
  // fires but before the React state has propagated.
  useEffect(() => {
    if (!isAuthLoading || !isAuthLoadingTimedOut || hasTriedTimeoutRehydrate) {
      return;
    }

    let active = true;
    setHasTriedTimeoutRehydrate(true);
    setIsRehydrating(true);

    void (async () => {
      try {
        await rehydrateSession();
      } finally {
        if (active) {
          setIsRehydrating(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [isAuthLoading, isAuthLoadingTimedOut, hasTriedTimeoutRehydrate, rehydrateSession]);

  // Wait for auth check and any targeted rehydration attempt.
  // Only throw if we've already exhausted the timeout rehydration attempt.
  if (isAuthLoading && isAuthLoadingTimedOut && hasTriedTimeoutRehydrate && !isRehydrating) {
    throw createSessionVerificationTimeoutError(
      "Session verification is taking longer than expected while initializing your session.",
    );
  }

  if (isAuthLoading) {
    return <ForgePageLoader />;
  }

  // A failed INITIAL_SESSION gets one rehydrate attempt above. Persistent or
  // unverified failures reach the error boundary instead of looking like a logout.
  if (hasSessionError && !isAuthenticated && !hasConfirmedSignedOutState) {
    const errorMessage = authError?.message || "Session check failed";
    const error = new Error(`Session verification failed: ${errorMessage}`);
    error.name = "SessionVerificationError";
    throw error;
  }

  if (!isAuthenticated) {
    const redirectReason = getAuthRedirectReason(lastAuthEvent);

    // Log the auth transition with full context
    logAuthTransition(redirectReason, {
      lastAuthEvent,
      fromRoute: currentRoute,
    });

    const authTarget = getAuthRedirectTarget(location);
    return <Navigate to={authTarget} state={{ from: location }} replace />;
  }

  return (
    <AuthenticatedProtectedRouteContent
      skipOrgCheck={skipOrgCheck}
      skipMfaCheck={skipMfaCheck}
      skipAccessError={skipAccessError}
      location={location}
      guardReadyMarkRef={guardReadyMarkRef}
      guardReadyLoggedRef={guardReadyLoggedRef}
    >
      {children}
    </AuthenticatedProtectedRouteContent>
  );
}
